import { Alert, Spin, Typography } from 'antd'
import {
  formatFfsScalar,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useFfsDisplayUnits } from 'features/indicationTriage/cards/ffsCalc/useFfsDisplayUnits'
import { DescribedTerm } from 'features/libra/components/DescribedTerm'
import { ObservedMinThickness } from 'features/libra/stepComponents/ffs/useObservedMinThickness'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'
import { GeckoMeasurementUnit } from 'types'

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0;
`

const Label = styled.span`
  min-width: 11rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`

const Value = styled.span`
  font-family:
    ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const StatusRow = styled.div<{ $pass: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin-top: 0.5rem;
  border-radius: 4px;
  background: ${({ $pass }) => ($pass ? '#f6ffed' : '#fff2f0')};
  border: 1px solid ${({ $pass }) => ($pass ? '#b7eb8f' : '#ffccc7')};
  color: ${({ $pass }) => ($pass ? '#389e0d' : '#cf1322')};
  font-weight: 600;
`

export interface ObservedMinThicknessCheckPanelProps {
  tMinMm: number | null
  observed: ObservedMinThickness
  /**
   * Derived by the caller, which also needs it for step validation -- passing
   * it keeps that comparison in one place rather than repeating it here.
   */
  pass: boolean | null
  /** Optional hover description for the "Required (t_min)" row label. */
  requiredDescription?: ReactNode
  /** Optional hover description for the "Observed minimum (CDS)" row label. */
  observedDescription?: ReactNode
}

// Inputs are canonical millimetres; the pass/fail comparison is done by the
// caller in mm. Here we only convert for display to the preferred unit.
const fmtThickness = (mm: number | null, unit: GeckoMeasurementUnit): string =>
  mm === null
    ? '--'
    : `${formatFfsScalar(mm, WORKING_UNIT_THICKNESS, unit)} ${unit.name}`

/**
 * Compares the inspection's observed minimum thickness (sourced from the
 * thickness CDF on the CDS) against the computed `t_min` for the Minimum
 * Thickness Analysis step. Pure presentational -- the caller passes in
 * pre-computed values.
 */
export const ObservedMinThicknessCheckPanel: FC<
  ObservedMinThicknessCheckPanelProps
> = ({ tMinMm, observed, pass, requiredDescription, observedDescription }) => {
  const { thickness: thicknessUnit } = useFfsDisplayUnits()
  switch (observed.status) {
    case 'loading':
      return (
        <div>
          <Spin size='small' /> Loading observed minimum...
        </div>
      )
    case 'noCdf':
      return (
        <Alert
          type='info'
          showIcon
          message='No thickness CDF available for this inspection.'
          description='The check needs a thickness component-data-frame produced by Conduit. Once the inspection has thickness data, this section will populate.'
        />
      )
    case 'unreadable':
      return (
        <Alert
          type='warning'
          showIcon
          message='Could not read thickness data for this inspection.'
          description='The thickness data frame exists but its readings could not be loaded, so the observed minimum could not be computed. This usually means the underlying data file is missing. Reload to retry.'
        />
      )
  }

  return (
    <>
      <Row>
        <Label>
          <DescribedTerm description={requiredDescription}>
            Required (t_min)
          </DescribedTerm>
        </Label>
        <Value>{fmtThickness(tMinMm, thicknessUnit)}</Value>
      </Row>
      <Row>
        <Label>
          <DescribedTerm description={observedDescription}>
            Observed minimum (CDS)
          </DescribedTerm>
        </Label>
        <Value>{fmtThickness(observed.observedMinMm, thicknessUnit)}</Value>
      </Row>
      {pass !== null ? (
        <StatusRow $pass={pass}>
          {pass ? 'PASS' : 'FAIL'} -- observed minimum {pass ? '>=' : '<'}{' '}
          required t_min
        </StatusRow>
      ) : (
        <Typography.Text type='secondary'>
          Enter the inputs above to compute the required t_min.
        </Typography.Text>
      )}
    </>
  )
}
